import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { closeModal } from '../actions/modal';
import { AsyncSelectField, SelectField } from '../containers/Form';
import Wizard from '../containers/Modal/Wizard';
import Strings from '../Strings';
import { defaultLanguage, supportedLanguages } from '../utils/language';
import { fileActions } from '../actions/files';
import { DEVICE_INSTRUCTION_VIDEO_PURPOSE, deviceInstructionActions } from '../pages/Organizations/Configuration/deviceInstructionActions';
import { selectorKey, selectorLabel } from '../pages/Organizations/Configuration/deviceInstructionHelpers';
import './FileModal.scss';

export const ASSIGN_DEVICE_INSTRUCTION_VIDEO_MODAL_NAME = 'assign-device-instruction-video';

const isSuccess = response => response?.type?.endsWith('/result');
const responseError = (response, fallback) => response?.response?.data?.error?.message || fallback;

function AssignDeviceInstructionVideoModal({ data, onCancel, searchModels, getStorageFiles, getOrganizations, getConfiguration, previewStoredFile }) {
  const fixedTarget = data?.target;
  const [language, setLanguage] = React.useState(fixedTarget?.language || data?.language || defaultLanguage);
  const [scope, setScope] = React.useState('global');
  const [organization, setOrganization] = React.useState(null);
  const [organizations, setOrganizations] = React.useState(null);
  const [organizationsLoading, setOrganizationsLoading] = React.useState(false);
  const [organizationsError, setOrganizationsError] = React.useState('');
  const [targetConfiguration, setTargetConfiguration] = React.useState(null);
  const [targetConfigurationLoading, setTargetConfigurationLoading] = React.useState(false);
  const [targetConfigurationError, setTargetConfigurationError] = React.useState('');
  const [kind, setKind] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [storedName, setStoredName] = React.useState(data?.initialObjectName || '');
  const [storageFiles, setStorageFiles] = React.useState(null);
  const [storageLoading, setStorageLoading] = React.useState(false);
  const [storageError, setStorageError] = React.useState('');
  const [storedPreview, setStoredPreview] = React.useState('');
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const previewRequest = React.useRef(0);
  const [deviceModels, setDeviceModels] = React.useState(null);
  const [modelLoading, setModelLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');
  const [submitError, setSubmitError] = React.useState('');
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightInvalidFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const targetOrganizationId = data?.organizationId || (scope === 'organization' ? organization?.value : null);

  const loadOrganizations = React.useCallback(async search => {
    if (!search) setOrganizationsLoading(true);
    setOrganizationsError('');
    try {
      const response = await getOrganizations(search);
      if (!isSuccess(response) || !Array.isArray(response.response?.data)) {
        throw new Error(responseError(response, Strings.deviceInstructionOrganizationsError));
      }
      const options = response.response.data
        .filter(item => item.masked_id)
        .map(item => ({ value: item.masked_id, label: item.name }));
      if (!search) setOrganizations(options);
      return options;
    } catch (error) {
      setOrganizationsError(error.message || Strings.deviceInstructionOrganizationsError);
      return [];
    } finally {
      if (!search) setOrganizationsLoading(false);
    }
  }, [getOrganizations]);

  React.useEffect(() => {
    if (!data?.organizationId && scope === 'organization' && organizations === null && !organizationsLoading && !organizationsError) {
      loadOrganizations('');
    }
  }, [data?.organizationId, scope, organizations, organizationsLoading, organizationsError, loadOrganizations]);

  React.useEffect(() => {
    if (!fixedTarget && !data?.organizationId && scope === 'organization' && organization?.value) {
      let current = true;
      setTargetConfiguration(null);
      setTargetConfigurationError('');
      setTargetConfigurationLoading(true);
      getConfiguration(organization.value, language).then(response => {
        if (!current) return;
        if (isSuccess(response) && Array.isArray(response.response?.data)) {
          setTargetConfiguration(response.response.data.map(item => ({ ...item, language })));
        } else {
          setTargetConfigurationError(responseError(response, Strings.deviceInstructionLoadError));
        }
        setTargetConfigurationLoading(false);
      }).catch(() => {
        if (!current) return;
        setTargetConfigurationError(Strings.deviceInstructionLoadError);
        setTargetConfigurationLoading(false);
      });
      return () => { current = false; };
    }
    setTargetConfiguration(null);
    setTargetConfigurationLoading(false);
    setTargetConfigurationError('');
    return undefined;
  }, [fixedTarget, data?.organizationId, scope, organization?.value, language, getConfiguration]);

  const loadStorageFiles = React.useCallback(async () => {
    setStorageLoading(true);
    setStorageError('');
    try {
      const response = await getStorageFiles();
      if (!isSuccess(response) || !Array.isArray(response.response?.data)) {
        setStorageFiles(null);
        setStorageError(responseError(response, Strings.deviceInstructionStorageLoadError));
      } else {
        setStorageFiles(response.response.data.filter(item => item.can_assign));
      }
    } catch (error) {
      setStorageFiles(null);
      setStorageError(Strings.deviceInstructionStorageLoadError);
    } finally {
      setStorageLoading(false);
    }
  }, [getStorageFiles]);

  React.useEffect(() => {
    if (storageFiles === null && !storageLoading && !storageError) loadStorageFiles();
  }, [storageFiles, storageLoading, storageError, loadStorageFiles]);

  const loadModels = React.useCallback(async () => {
    setModelLoading(true);
    setLoadError('');
    try {
      const limit = 100;
      const fetchPage = async (offset, models) => {
        const response = await searchModels('', offset, limit);
        if (!isSuccess(response) || !Array.isArray(response.response?.data)) {
          throw new Error(responseError(response, Strings.deviceInstructionTargetsError));
        }
        const nextModels = models.concat(response.response.data);
        const nextOffset = offset + response.response.data.length;
        if (response.response.data.length === 0 || nextOffset >= response.response.totalRecords) return nextModels;
        return fetchPage(nextOffset, nextModels);
      };
      const models = await fetchPage(0, []);
      setDeviceModels(models.filter(model => !model.deleted && model.device_type && model.device_type !== 'default'));
    } catch (error) {
      setLoadError(error.message || Strings.deviceInstructionTargetsError);
    } finally {
      setModelLoading(false);
    }
  }, [searchModels]);

  React.useEffect(() => {
    if (!fixedTarget && kind && deviceModels === null && !modelLoading && !loadError) loadModels();
  }, [fixedTarget, kind, deviceModels, modelLoading, loadError, loadModels]);

  const typeOptions = [...new Set((deviceModels || []).map(model => model.device_type))].sort().map(deviceType => {
    const selector = { selector_kind: 'type', device_type: deviceType };
    return { value: deviceType, label: selectorLabel(selector), selector };
  });
  const modelOptions = (deviceModels || []).map(model => {
    const selector = {
      selector_kind: 'model',
      device_model_id: model.id,
      manufacturer: model.manufacturer,
      model: model.model,
      device_type: model.device_type,
    };
    return { value: model.id, label: selectorLabel(selector), selector };
  });
  const selector = fixedTarget || selected?.selector;
  const existing = !data?.organizationId && scope === 'organization' ? targetConfiguration : data?.existing;
  const duplicateOwn = !fixedTarget && selector && existing?.some(item =>
    selectorKey(item) === selectorKey(selector) && item.language === language && item.own_object_name);
  const availableFiles = storageFiles?.filter(item => !fixedTarget || item.object_name !== fixedTarget.own_object_name);
  const validStoredName = availableFiles?.some(item => item.object_name === storedName);

  React.useEffect(() => {
    const errors = [];
    if (!data?.organizationId && scope === 'organization' && !organization) {
      errors.push({ property: 'organization', propertyName: Strings.organization, missing: true });
    }
    if (!fixedTarget && !kind) errors.push({ property: 'kind', propertyName: Strings.deviceInstructionSelectorKind, missing: true });
    if (!selector && kind) errors.push({ property: 'target', propertyName: Strings.deviceInstructionSelector, missing: true });
    if (!validStoredName) errors.push({ property: 'storedVideo', propertyName: Strings.deviceInstructionStoredVideo, missing: true });
    if (duplicateOwn) errors.push({ property: 'target', propertyName: Strings.deviceInstructionSelector, errors: [Strings.deviceInstructionAlreadyConfigured], missing: false });
    setValidationErrors(errors);
  }, [data?.organizationId, scope, organization, fixedTarget, kind, selector, validStoredName, duplicateOwn]);

  const showStoredPreview = async () => {
    if (!storedName || previewLoading) return;
    if (storedPreview) {
      setStoredPreview('');
      return;
    }
    setPreviewLoading(true);
    setSubmitError('');
    const objectName = storedName;
    previewRequest.current += 1;
    const request = previewRequest.current;
    try {
      const response = await previewStoredFile(objectName);
      if (request !== previewRequest.current) return;
      if (isSuccess(response) && response.response?.url) setStoredPreview(response.response.url);
      else setSubmitError(responseError(response, Strings.deviceInstructionStoredPreviewError));
    } catch (error) {
      if (request === previewRequest.current) setSubmitError(Strings.deviceInstructionStoredPreviewError);
    } finally {
      if (request === previewRequest.current) setPreviewLoading(false);
    }
  };

  const onSubmit = async () => {
    if (isSaving || !selector || !validStoredName || duplicateOwn
      || (!data?.organizationId && scope === 'organization' && (!organization || targetConfiguration === null))
      || targetConfigurationLoading || targetConfigurationError) return;
    setIsSaving(true);
    setSubmitError('');
    try {
      if (await data.onAssign(selector, storedName, language, targetOrganizationId, setSubmitError)) onCancel();
    } catch (error) {
      setSubmitError(Strings.deviceInstructionStoredAssignError);
    } finally {
      setIsSaving(false);
    }
  };

  const pages = [{
    id: 'assign-device-instruction-video-page',
    title: fixedTarget ? Strings.deviceInstructionReplace : Strings.deviceInstructionAssignStored,
    content: (
      <>
        {!fixedTarget && !data?.organizationId && (
          <>
            <SelectField
              id="device-instruction-scope"
              name="device-instruction-scope"
              label={Strings.deviceInstructionAssignAt}
              value={scope}
              onChange={option => {
                setScope(option?.value || 'global');
                setOrganization(null);
                setTargetConfiguration(null);
                setSubmitError('');
              }}
              data={[
                { value: 'global', label: Strings.deviceInstructionGlobal },
                { value: 'organization', label: Strings.organization },
              ]}
              isRequired
              highlightInvalid={highlightInvalidFields}
            />
            {scope === 'organization' && (
              <>
                {organizationsLoading && <p>{Strings.loading}</p>}
                {organizationsError && (
                  <p role="alert">
                    {organizationsError}
                    <button type="button" onClick={() => loadOrganizations('')}>{Strings.deviceInstructionTryAgain}</button>
                  </p>
                )}
                <AsyncSelectField
                  id="device-instruction-organization"
                  name="device-instruction-organization"
                  label={Strings.organization}
                  placeholder={Strings.deviceInstructionSelectOrganization}
                  value={organization}
                  onChange={option => {
                    setOrganization(option);
                    setTargetConfiguration(null);
                    setSubmitError('');
                  }}
                  defaultOptions={organizations || []}
                  loadOptions={loadOrganizations}
                  cacheOptions={false}
                  noOptionsMessage={() => Strings.noOrganizationsFound}
                  isRequired
                  highlightInvalid={highlightInvalidFields}
                />
                {targetConfigurationLoading && <p>{Strings.loading}</p>}
                {targetConfigurationError && <p role="alert">{targetConfigurationError}</p>}
              </>
            )}
          </>
        )}
        {fixedTarget ? (
          <p>{Strings.formatString(Strings.deviceInstructionReplaceTarget, selectorLabel(fixedTarget), Strings.languagesEnum?.[language] || language)}</p>
        ) : (
          <>
            <SelectField
              id="device-instruction-language"
              name="device-instruction-language"
              label={Strings.language}
              value={language}
              onChange={option => {
                setLanguage(option?.value || defaultLanguage);
                setTargetConfiguration(null);
              }}
              data={supportedLanguages.map(item => ({ value: item.id, label: Strings.languagesEnum?.[item.id] || item.id }))}
              isRequired
              highlightInvalid={highlightInvalidFields}
            />
            <SelectField
              id="device-instruction-kind"
              name="device-instruction-kind"
              label={Strings.deviceInstructionSelectorKind}
              placeholder={Strings.deviceInstructionSelectKind}
              value={kind}
              onChange={option => {
                setKind(option?.value || '');
                setSelected(null);
                setSubmitError('');
              }}
              data={[
                { value: 'type', label: Strings.deviceInstructionDeviceType },
                { value: 'model', label: Strings.deviceInstructionModel },
              ]}
              isRequired
              highlightInvalid={highlightInvalidFields}
            />
            {kind && modelLoading && <p>{Strings.loading}</p>}
            {kind && loadError && (
              <p role="alert">
                {loadError}
                <button type="button" onClick={loadModels}>{Strings.deviceInstructionTryAgain}</button>
              </p>
            )}
            {kind === 'type' && !modelLoading && !loadError && (
              <SelectField
                id="device-instruction-type"
                name="device-instruction-type"
                label={Strings.deviceInstructionDeviceType}
                placeholder={Strings.deviceInstructionSelectType}
                value={selected?.value}
                onChange={option => { setSelected(option); setSubmitError(''); }}
                data={typeOptions}
                isSearchable
                isRequired
                highlightInvalid={highlightInvalidFields}
              />
            )}
            {kind === 'model' && !modelLoading && !loadError && (
              <SelectField
                id="device-instruction-model"
                name="device-instruction-model"
                label={Strings.deviceInstructionModel}
                placeholder={Strings.deviceInstructionSearchModels}
                value={selected?.value}
                onChange={option => { setSelected(option); setSubmitError(''); }}
                data={modelOptions}
                isSearchable
                isRequired
                highlightInvalid={highlightInvalidFields}
              />
            )}
          </>
        )}
        {duplicateOwn && <p role="alert">{Strings.deviceInstructionAlreadyConfigured}</p>}
        {storageLoading && <p>{Strings.deviceInstructionLoadingStorage}</p>}
        {storageError && (
          <p role="alert">
            {storageError}
            <button type="button" onClick={loadStorageFiles}>{Strings.deviceInstructionTryAgain}</button>
          </p>
        )}
        {availableFiles && (availableFiles.length === 0 ? <p>{Strings.deviceInstructionNoStoredVideos}</p> : (
          <SelectField
            id="device-instruction-stored-video"
            name="device-instruction-stored-video"
            label={Strings.deviceInstructionStoredVideo}
            placeholder={Strings.deviceInstructionSelectStoredVideo}
            value={storedName}
            onChange={option => {
              setStoredName(option?.value || '');
              previewRequest.current += 1;
              setPreviewLoading(false);
              setStoredPreview('');
              setSubmitError('');
            }}
            data={availableFiles.map(item => ({ value: item.object_name, label: item.object_name }))}
            isSearchable
            isRequired
            highlightInvalid={highlightInvalidFields}
          />
        ))}
        {validStoredName && (
          <button
            type="button"
            className="brand-blue file-modal__preview-button"
            disabled={previewLoading}
            onClick={showStoredPreview}
          >
            {previewLoading ? Strings.loading : storedPreview ? Strings.deviceInstructionHidePreview : Strings.deviceInstructionPreview}
          </button>
        )}
        {storedPreview && <video controls src={storedPreview} aria-label={Strings.formatString(Strings.deviceInstructionStoredPreview, storedName)} />}
        {submitError && <p role="alert">{submitError}</p>}
      </>
    ),
    nextButton: { text: isSaving ? Strings.deviceInstructionAssigning : fixedTarget ? Strings.deviceInstructionReplace : Strings.deviceInstructionAssignStored },
    emptyFieldsCount: validationErrors.filter(error => error.missing).length,
    canGoNext: !isSaving && Boolean(selector) && Boolean(validStoredName) && validationErrors.length === 0
      && !modelLoading && !loadError
      && (data?.organizationId || scope !== 'organization' || targetConfiguration !== null)
      && !targetConfigurationLoading && !targetConfigurationError,
  }];

  return <Wizard name={ASSIGN_DEVICE_INSTRUCTION_VIDEO_MODAL_NAME} pages={pages} onSubmit={onSubmit} onNextButtonHover={setHighlightInvalidFields} />;
}

AssignDeviceInstructionVideoModal.propTypes = {
  data: PropTypes.shape({
    existing: PropTypes.array,
    language: PropTypes.string,
    organizationId: PropTypes.string,
    initialObjectName: PropTypes.string,
    target: PropTypes.object,
    onAssign: PropTypes.func.isRequired,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
  searchModels: PropTypes.func.isRequired,
  getStorageFiles: PropTypes.func.isRequired,
  getOrganizations: PropTypes.func.isRequired,
  getConfiguration: PropTypes.func.isRequired,
  previewStoredFile: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  onCancel: () => dispatch(closeModal(ASSIGN_DEVICE_INSTRUCTION_VIDEO_MODAL_NAME)),
  searchModels: (search, offset, limit) => dispatch(deviceInstructionActions.searchModels(search, offset, limit)),
  getStorageFiles: () => dispatch(fileActions.getStorageFiles(DEVICE_INSTRUCTION_VIDEO_PURPOSE)),
  getOrganizations: search => dispatch(deviceInstructionActions.searchOrganizations(search)),
  getConfiguration: (orgId, language) => dispatch(deviceInstructionActions.getConfiguration(orgId, language)),
  previewStoredFile: objectName => dispatch(fileActions.previewStoredFile(DEVICE_INSTRUCTION_VIDEO_PURPOSE, objectName)),
});

export default connect(null, mapDispatchToProps)(AssignDeviceInstructionVideoModal);
